import { useEffect, useMemo, useState } from 'react'
import { ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline'
import SearchableSelect, { type SelectOption } from '@/components/SearchableSelect'
import { getQBOTokenStatus, getVendors, type QBOVendor } from '@/services/qbo'

function parseExcludedVendorIds(raw: string): string[] {
  if (!raw?.trim()) return []
  try {
    const parsed = JSON.parse(raw) as unknown
    if (Array.isArray(parsed)) {
      return parsed.filter((x): x is string => typeof x === 'string')
    }
  } catch {
    /* ignore invalid JSON */
  }
  return []
}

type Props = {
  excludedVendorIdsRaw: string
  saving: boolean
  onSave: (value: string) => Promise<void>
}

export default function VendorAutoCategorizationExclusions({
  excludedVendorIdsRaw,
  saving,
  onSave,
}: Props) {
  const [qboConnected, setQboConnected] = useState<boolean | null>(null)
  const [exclusionVendors, setExclusionVendors] = useState<QBOVendor[]>([])
  const [exclusionVendorsLoading, setExclusionVendorsLoading] = useState(false)
  const [exclusionAddPickerKey, setExclusionAddPickerKey] = useState(0)

  const excludedVendorIds = useMemo(
    () => parseExcludedVendorIds(excludedVendorIdsRaw),
    [excludedVendorIdsRaw]
  )

  useEffect(() => {
    void (async () => {
      try {
        const status = await getQBOTokenStatus()
        setQboConnected(status.valid)
      } catch {
        setQboConnected(false)
      }
    })()
  }, [])

  useEffect(() => {
    if (!qboConnected) {
      setExclusionVendors([])
      return
    }
    void loadExclusionVendors(false)
  }, [qboConnected])

  async function loadExclusionVendors(refresh: boolean) {
    try {
      setExclusionVendorsLoading(true)
      const list = await getVendors(refresh)
      setExclusionVendors(list.filter((v) => v.active))
    } catch (err) {
      console.error('Failed to load vendors for exclusions:', err)
    } finally {
      setExclusionVendorsLoading(false)
    }
  }

  const exclusionVendorIdsKnown = useMemo(
    () => new Set(exclusionVendors.map((v) => v.id)),
    [exclusionVendors]
  )

  const orphanExcludedVendorIds = useMemo(
    () => excludedVendorIds.filter((id) => !exclusionVendorIdsKnown.has(id)),
    [excludedVendorIds, exclusionVendorIdsKnown]
  )

  const exclusionAddOptions = useMemo((): SelectOption[] => {
    return exclusionVendors
      .filter((v) => !excludedVendorIds.includes(v.id))
      .map((v) => ({ id: v.id, label: v.display_name }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
  }, [exclusionVendors, excludedVendorIds])

  async function setVendorExcluded(vendorId: string, excluded: boolean) {
    const current = parseExcludedVendorIds(excludedVendorIdsRaw)
    const next = excluded
      ? [...new Set([...current, vendorId])]
      : current.filter((id) => id !== vendorId)
    await onSave(JSON.stringify(next))
  }

  if (qboConnected === null) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <ArrowPathIcon className="h-4 w-4 shrink-0 animate-spin" />
        Checking QuickBooks connection…
      </div>
    )
  }

  if (!qboConnected) {
    return (
      <p className="text-sm text-gray-500">
        Connect QuickBooks under Settings to manage vendor auto-categorization exclusions.
      </p>
    )
  }

  if (exclusionVendorsLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <ArrowPathIcon className="h-4 w-4 shrink-0 animate-spin" />
        Loading vendors…
      </div>
    )
  }

  return (
    <div className="w-full space-y-4">
      <div>
        <label className="mb-1.5 block text-xs font-medium text-gray-600" htmlFor="settings-exclusion-add-vendor">
          Add vendor to exclude
        </label>
        <SearchableSelect
          key={exclusionAddPickerKey}
          options={exclusionAddOptions}
          value={null}
          onChange={(id) => {
            if (!id) return
            void setVendorExcluded(id, true).then(() => setExclusionAddPickerKey((k) => k + 1))
          }}
          placeholder={
            exclusionVendors.length === 0
              ? 'No vendors loaded—connect and sync first'
              : exclusionAddOptions.length === 0
                ? 'All active vendors are already excluded'
                : 'Search vendors to exclude…'
          }
          disabled={saving || exclusionAddOptions.length === 0}
          className="w-full max-w-[800px]"
        />
      </div>
      <div>
        <p className="mb-2 text-xs font-medium text-gray-600">Excluded ({excludedVendorIds.length})</p>
        {excludedVendorIds.length === 0 ? (
          <p className="text-sm text-gray-500">None yet.</p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {excludedVendorIds.map((id) => {
              const known = exclusionVendors.find((v) => v.id === id)
              const isOrphan = !known
              return (
                <li key={id}>
                  <span
                    className={`inline-flex max-w-full items-center gap-1 rounded-full py-1 pl-2.5 pr-1 text-sm ${
                      isOrphan
                        ? 'bg-amber-50 text-amber-950 ring-1 ring-amber-200/80'
                        : 'bg-gray-100 text-gray-800 ring-1 ring-gray-200/80'
                    }`}
                    title={isOrphan ? id : undefined}
                  >
                    <span className="max-w-[12rem] truncate sm:max-w-[16rem]">
                      {known?.display_name ?? 'Unknown vendor'}
                    </span>
                    {isOrphan ? (
                      <code className="hidden max-w-[6rem] truncate text-[10px] text-amber-900/90 sm:inline">
                        {id}
                      </code>
                    ) : null}
                    <button
                      type="button"
                      className="rounded-full p-0.5 text-gray-500 transition-colors hover:bg-black/5 hover:text-gray-800"
                      aria-label={known ? `Remove ${known.display_name}` : `Remove exclusion ${id}`}
                      onClick={() => void setVendorExcluded(id, false)}
                      disabled={saving}
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </div>
      {orphanExcludedVendorIds.length > 0 ? (
        <p className="text-xs leading-relaxed text-amber-900/90">
          <span className="font-medium text-amber-950">Note:</span> Some exclusions reference vendors that are not in
          your current QuickBooks list (for example after a merge). Remove them with the × on the chip if they should
          no longer apply.
        </p>
      ) : null}
    </div>
  )
}
